import { useEffect, useRef, useCallback } from "react";

export function useInfiniteScroll({
    hasNextPage,
    getNextPage,
    options = { root: null, rootMargin: "500px", threshold: 0.1 },
}) {
    const observerRef = useRef(null);
    const sentinelRef = useRef(null);
    const loadingRef = useRef(false);

    const loadMore = useCallback(async () => {
        if (!hasNextPage || loadingRef.current) return;

        try {
            loadingRef.current = true;
            await getNextPage();
        } finally {
            loadingRef.current = false;
        }
    }, [hasNextPage, getNextPage]);

    useEffect(() => {
        if (!sentinelRef.current) return;

        observerRef.current = new IntersectionObserver(
            (entries) => {
                const entry = entries[0];
                if (entry.isIntersecting) loadMore();
            },
            {
                root: options.root ?? null,
                rootMargin: options.rootMargin ?? "500px",
                threshold: options.threshold ?? 0.1,
            }
        );

        observerRef.current.observe(sentinelRef.current);

        return () => {
            observerRef.current?.disconnect();
        };
    }, [loadMore, options.root, options.rootMargin, options.threshold]);

    return {
        sentinelRef,
        loading: loadingRef.current,
    };
}
